/**
 * DryStep (ydm) reels row (sections/ydm-reels.liquid), ported from the Titanium Caravans model reels
 * (clients/titanium-caravans/wordpress/theme/assets/js/reels.js, signed off by Jack 24 September 2026).
 *
 * Mouse screens: every tile is positioned from the centre by transforms; the
 * active tile sits in the middle at full height and plays, the rest step out
 * either side at the shorter height. A mouse drag or a trackpad's two-finger
 * swipe moves the row with the hand and settles on the nearest reel (the
 * trackpad swipe is the one addition to the Titanium row, Jack 16 October
 * 2026: "on desktop at the moment you can't flick or scroll them"). Touch
 * screens (.is-native): the browser's own scroll-snap row, centred, each tile
 * scaled by its distance from the centre as the row moves. Both loop, using
 * hidden copies of the set.
 *
 * Playback: only the centre reel plays, muted until the sound button is
 * pressed. A stopped reel keeps its frame on a canvas and lets go of its
 * download; a little after the playing one starts, the next reel along loads
 * its opening frame so the next move starts at once. Nothing plays until the
 * row is on screen, and it pauses when the row scrolls away or the tab is
 * hidden. Reduced motion: no autoplay, a click on the centre plays.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function px(style, name) {
    return parseFloat(style.getPropertyValue(name)) || 0;
  }

  function Reels(root) {
    this.root = root;
    this.stage = root.querySelector('.ydm-reels__stage');
    this.tiles = Array.prototype.slice.call(root.querySelectorAll('.ydm-reel'));
    this.n = this.tiles.length;
    this.count = this.n; // the real reels; tiles past this are copies (see ensureLoop)
    this.track = root.querySelector('.ydm-reels__track');
    // Touch screens get a native scroll-snap row, the same as the Our Range row
    // on phones: the browser owns the swipe, momentum and axis lock, so the page
    // never bounces. Mouse screens keep the centred carousel.
    this.native = !!(window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches);
    this.nav = root.querySelector('.ydm-reels__nav');
    this.prev = root.querySelector('[data-reels-prev]');
    this.next = root.querySelector('[data-reels-next]');
    this.dotsWrap = root.querySelector('.ydm-reels__dots');
    this.dots = [];
    this.active = 0;
    this.frac = 0;
    this.sound = false;
    this.holdSound = false;
    this.inView = false;
    this.userPlay = false;
    // Native row, per move: where the scroll started and whether the reels
    // have already been handed over (an arrow or dot does it straight away).
    this.from = null;
    this.handed = false;
    this.dir = 1; // the direction of the last move, for prime()
    this.primed = null;
    this.init();
  }

  Reels.prototype.measure = function () {
    var s = window.getComputedStyle(this.root);
    if (this.native) {
      this.pitch = px(s, '--reel-w') + (parseFloat(window.getComputedStyle(this.track).columnGap) || 0);
      this.visible = Math.ceil(this.track.clientWidth / this.pitch);
      this.loop = false;
      return;
    }
    this.w = px(s, '--reel-w');
    this.h = px(s, '--reel-h');
    this.hc = px(s, '--reel-hc');
    this.pitch = this.w + px(s, '--reel-gap');
    var half = this.stage.clientWidth / 2;
    this.visible = Math.ceil((half + this.w / 2) / this.pitch);
    // Loop only when a tile can wrap from one side to the other off screen.
    this.loop = this.n > this.visible * 2 + 1;
  };

  // Reserve as many layout lines on every card as the longest one needs.
  Reels.prototype.fitLabels = function () {
    var labels = this.root.querySelectorAll('.ydm-reel__sub');
    var max = 1;
    this.root.style.setProperty('--reel-lines', '1');
    Array.prototype.forEach.call(labels, function (l) {
      var lh = parseFloat(window.getComputedStyle(l).lineHeight) || 1;
      max = Math.max(max, Math.round(l.scrollHeight / lh));
    });
    this.root.style.setProperty('--reel-lines', String(max));
    // Dots only while they fit on one line between the arrows.
    if (this.dotsWrap && this.dots.length) {
      this.dotsWrap.hidden = false;
      this.dotsWrap.hidden = this.dotsWrap.scrollWidth > this.dotsWrap.clientWidth + 1;
    }
  };

  // A reel whose video request has hung (no data at all): swap in a fresh
  // video element and ask again, since re-asking the stuck element does not
  // always recover it. At most twice per reel.
  Reels.prototype.retry = function (tile, why) {
    var old = tile._video;
    var src = old && old.getAttribute('src');
    tile._retries = (tile._retries || 0) + 1;
    if (!src || tile._retries > 2) {
      return;
    }
    this.lastPlayError = why;
    var fresh = old.cloneNode(false);
    fresh.removeAttribute('src');
    old.removeAttribute('src');
    old.load();
    old.parentNode.replaceChild(fresh, old);
    tile._video = fresh;
    this.bindVideo(tile);
    fresh.muted = old.muted;
    fresh.preload = 'auto';
    fresh.src = src;
    fresh.play().catch(function () {});
  };

  Reels.prototype.setupTile = function (t) {
    t._media = t.querySelector('.ydm-reel__media');
    t._video = t.querySelector('video');
    t._card = t.querySelector('a.ydm-reel__main'); // the card's model link, the one kept in the tab order (none on the USA page)
    t._sound = t.querySelector('.ydm-reel__sound');
    t._snap = t.querySelector('.ydm-reel__snap');
    if (t._video) {
      this.bindVideo(t);
    }
  };

  Reels.prototype.bindVideo = function (t) {
    var reels = this;
    var v = t._video;
    v.addEventListener('stalled', function () {
      if (reels.tiles[reels.active] === t && t._video === v && v.readyState < 2 && !v.paused) {
        reels.retry(t, 'stalled');
      }
    });
    // Show the video over the cover (or the held frame) only once a real frame
    // is on screen; 'playing' can fire a beat before the first frame is painted.
    // Both show the frame the video starts on, so the change is invisible.
    v.addEventListener('playing', function () {
      if (reels.tiles[reels.active] === t) {
        reels.schedulePrime(1200);
      }
      if (v.requestVideoFrameCallback) {
        v.requestVideoFrameCallback(function () {
          if (!v.paused && t._video === v) {
            t.classList.add('is-playing');
          }
        });
      } else {
        t.classList.add('is-playing');
      }
    });
  };

  // A screen wider than the reels can fill (10 reels run out past about 2200px)
  // would leave the row stopping at its ends with an empty side. Repeat the
  // whole set, as hidden copies, until the row can loop. Copies are appended
  // in full sets, so tile i is always a copy of reel i % count.
  Reels.prototype.ensureLoop = function () {
    if (this.count < 3) {
      return;
    }
    if (this.native) {
      // Touch row: seven full sets and start in the middle one, so there are
      // reels either side from the first view and about thirty swipes each way
      // before the row needs its silent jump back to the middle (see settle()).
      this.sets = 7;
      while (this.tiles.length < this.count * this.sets) {
        this.cloneSet();
      }
      this.n = this.tiles.length;
      return;
    }
    var s = window.getComputedStyle(this.root);
    var w = px(s, '--reel-w');
    var pitch = w + px(s, '--reel-gap');
    var visible = Math.ceil((this.stage.clientWidth / 2 + w / 2) / pitch);
    while (this.tiles.length <= visible * 2 + 1) {
      this.cloneSet();
    }
    this.n = this.tiles.length;
  };

  Reels.prototype.cloneSet = function () {
    var track = this.track;
    for (var i = 0; i < this.count; i++) {
      var c = this.tiles[i].cloneNode(true);
      c.classList.remove('is-centre', 'is-playing', 'has-snap', 'no-anim');
      c.removeAttribute('aria-label');
      c.setAttribute('aria-hidden', 'true');
      c.style.transform = '';
      var snap = c.querySelector('.ydm-reel__snap');
      if (snap) {
        snap.parentNode.removeChild(snap);
      }
      var v = c.querySelector('video');
      if (v && !v.hasAttribute('data-keep')) {
        v.removeAttribute('src');
        v.preload = 'none';
      }
      Array.prototype.forEach.call(c.querySelectorAll('img'), function (img) {
        img.loading = 'lazy';
      });
      c._clone = true;
      this.setupTile(c);
      track.appendChild(c);
      this.tiles.push(c);
    }
  };

  // Shortest signed distance from the centre tile to tile t.
  Reels.prototype.wrap = function (d) {
    if (!this.loop) {
      return d;
    }
    d = ((d % this.n) + this.n) % this.n;
    return d >= this.n / 2 ? d - this.n : d;
  };

  // Go to reel k (a dot), taking whichever copy of it is nearest.
  Reels.prototype.goOrig = function (k) {
    var best = k;
    var bestD = Infinity;
    for (var t = k; t < this.n; t += this.count) {
      var d = Math.abs(this.wrap(t - this.active));
      if (d < bestD) {
        bestD = d;
        best = t;
      }
    }
    this.go(best);
  };

  Reels.prototype.offsetOf = function (i) {
    var d = i - this.active - this.frac;
    if (this.loop) {
      d = ((d % this.n) + this.n) % this.n;
      if (d >= this.n / 2) {
        d -= this.n;
      }
    }
    return d;
  };

  Reels.prototype.layout = function (instant) {
    var self = this;
    if (this.native) {
      return;
    }
    this.tiles.forEach(function (tile, i) {
      var d = self.offsetOf(i);
      var jumped = tile._d !== undefined && Math.abs(d - tile._d) > self.n / 2;
      tile._d = d;
      var ad = Math.min(Math.abs(d), 1);
      var hh = self.hc - (self.hc - self.h) * ad;
      // During a drag (instant === 'drag') the is-dragging class already stops
      // transitions, so only a tile wrapping to the other side is marked.
      if (instant === true || jumped) {
        tile.classList.add('no-anim');
      }
      tile.style.transform = 'translate3d(' + (d * self.pitch).toFixed(1) + 'px,' + ((self.hc - hh) / 2).toFixed(1) + 'px,0)';
      tile._media.style.height = hh.toFixed(1) + 'px';
      var far = tile._clone || Math.abs(d) > self.visible;
      tile.setAttribute('aria-hidden', far ? 'true' : 'false');
      if (tile._card) {
        tile._card.tabIndex = far ? -1 : 0;
      }
      if (tile._sound) {
        tile._sound.tabIndex = i === self.active && !tile._clone ? 0 : -1;
      }
    });
    if (instant === true || this.tiles.some(function (t) { return t.classList.contains('no-anim'); })) {
      // Let the jump land before transitions come back.
      void this.stage.offsetWidth;
      window.requestAnimationFrame(function () {
        self.tiles.forEach(function (t) { t.classList.remove('no-anim'); });
      });
    }
  };

  // Native row: how far tile i's centre sits from the row's centre, in px.
  // Tile centres are measured once (and on resize) rather than read off the
  // layout every frame: reading layout after writing a transform forces the
  // browser to recalculate it, 70 tiles a frame, which stutters on phones.
  Reels.prototype.cacheCentres = function () {
    this.centres = this.tiles.map(function (tile) {
      return tile.offsetLeft + tile.offsetWidth / 2;
    });
    this.half = this.track.clientWidth / 2;
  };

  Reels.prototype.centreOffset = function (i, scrollLeft) {
    if (!this.centres || this.centres.length !== this.n) {
      this.cacheCentres();
    }
    var sl = scrollLeft === undefined ? this.track.scrollLeft : scrollLeft;
    return this.centres[i] - sl - this.half;
  };

  Reels.prototype.tileLeft = function (i) {
    return this.track.scrollLeft + this.centreOffset(i);
  };

  Reels.prototype.nearest = function () {
    var best = 0;
    var bestD = Infinity;
    var sl = this.track.scrollLeft;
    for (var i = 0; i < this.n; i++) {
      var d = Math.abs(this.centreOffset(i, sl));
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  };

  // Native row: the centre reel full size, its neighbours slightly smaller,
  // following the finger frame by frame (a transform, so it stays smooth).
  Reels.prototype.scaleFrame = function () {
    var pitch = this.pitch || 1;
    var sl = this.track.scrollLeft; // the one layout read per frame
    for (var i = 0; i < this.n; i++) {
      var d = Math.abs(this.centreOffset(i, sl)) / pitch;
      var s = d >= 1 ? 0.88 : 1 - 0.12 * d;
      s = Math.round(s * 1000) / 1000;
      // Only touch tiles whose scale changes (in practice the two either
      // side of the centre); everything else is already at 0.88.
      if (this.tiles[i]._s !== s) {
        this.tiles[i]._s = s;
        this.tiles[i].style.transform = 'scale(' + s + ')';
      }
    }
  };

  // Native row at rest. If it stopped on a copy in an outer set, jump to the
  // same reel in the middle set (identical, so the jump is invisible) and
  // carry the playing reel's frame and time across. Then make sure the reel
  // it rests on is the playing one (a flick past more than one reel, or a
  // swipe that came back) and tidy up.
  Reels.prototype.settle = function () {
    var k = this.nearest();
    var mid = Math.floor(this.sets / 2) * this.count;
    if (this.sets && this.n === this.count * this.sets && (k < this.count || k >= this.n - this.count)) {
      var k2 = mid + (k % this.count);
      var tr = this.track;
      var snap = tr.style.scrollSnapType;
      tr.style.scrollSnapType = 'none';
      tr.scrollLeft = tr.scrollLeft + (this.tiles[k2].offsetLeft - this.tiles[k].offsetLeft);
      void tr.offsetWidth;
      tr.style.scrollSnapType = snap;
      var from = this.tiles[k];
      var to = this.tiles[k2];
      if (k === this.active && from._video && to._video && from.classList.contains('is-playing') && from._video.readyState >= 2) {
        this.snapshot(to, from._video);
        to._resume = from._video.currentTime;
      }
      k = k2;
      this.scaleFrame();
    }
    this.handover(k);
    this.markCentre();
    this.updateNav();
    this.tidy();
  };

  // The move starts and the incoming reel starts with it. On a touch row the
  // outgoing reel only pauses on its current frame and keeps its source until
  // tidy(): this runs inside the scroll handler, and copying the frame and
  // tearing down the download there costs a dropped frame of the scale that
  // follows the finger (measured at swipe start). On a mouse screen the slide
  // is a CSS transition, so the outgoing reel lets go of its download at once,
  // freeing the connection for the incoming one, and its switch to the held
  // frame lands while it is moving; at rest the switch could show, as a 1x
  // screen draws the held frame a touch sharper than the video.
  Reels.prototype.handover = function (i) {
    if (i === this.active) {
      return;
    }
    var self = this;
    var old = this.tiles[this.active];
    this.dir = this.wrap(i - this.active) < 0 ? -1 : 1;
    window.clearTimeout(this.primeTimer);
    // Besides the primed reel (and on a touch row the outgoing one), nothing
    // else may hold a download: a reel stopped by an earlier move that has not
    // been tidied yet (moves in quick succession) lets go now.
    this.tiles.forEach(function (t, k) {
      if (k !== i && t !== self.primed && (t !== old || !self.native)) {
        self.rest(t);
      }
    });
    if (old && old._video) {
      old._video.pause();
    }
    this.active = i;
    this.tiles[i]._userPaused = false;
    this.userPlay = false;
    // Mid swipe on a touch row, the centre marks (Shop Now, the sound
    // button) and the dots wait for the row to rest: changing them restyles
    // the cards and repaints the dots, and the scale transforms then wait for
    // that work in the very frames that follow the finger (measured: one
    // dropped frame at swipe start in most runs, none without).
    if (!(this.native && this.handed)) {
      this.markCentre();
      this.updateNav();
    }
    this.playActive();
  };

  Reels.prototype.go = function (i) {
    if (this.native) {
      i = Math.max(0, Math.min(this.n - 1, i));
      var left = this.tileLeft(i);
      if (Math.abs(this.track.scrollLeft - left) < 2) {
        this.handover(i);
        this.tidy();
        return;
      }
      // An arrow or dot names its reel, so hand over now rather than waiting
      // for the row to move; the scroll handler then leaves it alone.
      this.handed = true;
      this.holdSound = true;
      this.handover(i);
      this.updateNav(); // the tapped arrow or dot answers at once
      this.track.scrollTo({ left: left, behavior: reduce ? 'auto' : 'smooth' });
      return;
    }
    if (this.loop) {
      i = ((i % this.n) + this.n) % this.n;
    } else {
      i = Math.max(0, Math.min(this.n - 1, i));
    }
    // The incoming reel starts as the slide begins (Jack, 2026-09-24).
    if (i !== this.active) {
      this.handover(i);
    } else {
      this.playActive();
    }
    this.frac = 0;
    this.layout(false);
    // Tidy once the slide has landed.
    var self = this;
    window.clearTimeout(this.tidyTimer);
    this.tidyTimer = window.setTimeout(function () {
      self.tidy();
    }, (this.glide || 500) + 80);
  };

  Reels.prototype.setGlide = function (ms) {
    this.glide = Math.round(ms);
    this.root.style.setProperty('--reel-glide', this.glide + 'ms');
  };

  // The centre tile shows Shop Now and the sound button.
  Reels.prototype.markCentre = function () {
    var self = this;
    this.tiles.forEach(function (t, i) {
      t.classList.toggle('is-centre', i === self.active);
    });
  };

  Reels.prototype.updateNav = function () {
    var reel = this.active % this.count;
    this.dots.forEach(function (d, i) {
      d.classList.toggle('is-active', i === reel);
      d.setAttribute('aria-current', i === reel ? 'true' : 'false');
    });
    if (this.prev && this.next) {
      this.prev.disabled = !this.loop && this.active === 0;
      this.next.disabled = !this.loop && this.active === this.n - 1;
    }
  };

  // A stopped reel lets go of its download. If it was showing video, its
  // frame is copied to a canvas in the same spot first, so the picture does
  // not change, and it will resume from there.
  Reels.prototype.rest = function (tile) {
    var v = tile._video;
    if (!v || !v.getAttribute('src') || v.hasAttribute('data-keep')) {
      return;
    }
    if (!v.paused) {
      v.pause();
    }
    if (tile.classList.contains('is-playing') && v.readyState >= 2) {
      this.snapshot(tile, v);
      tile._resume = v.currentTime;
    }
    v.removeAttribute('src');
    v.load();
    tile.classList.remove('is-playing');
  };

  Reels.prototype.snapshot = function (tile, v) {
    var c = tile._snap;
    if (!c) {
      c = document.createElement('canvas');
      c.className = 'ydm-reel__snap';
      c.setAttribute('aria-hidden', 'true');
      tile._media.insertBefore(c, tile._video);
      tile._snap = c;
    }
    var w = v.videoWidth || 720;
    var h = v.videoHeight || 1280;
    var scale = Math.min(1, 720 / w);
    c.width = Math.round(w * scale);
    c.height = Math.round(h * scale);
    try {
      c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
      tile.classList.add('has-snap');
    } catch (e) {
      tile.classList.remove('has-snap');
    }
  };

  // The row is at rest: every reel but the playing one lets go of its
  // download, including a primed one the move did not use. Well off screen, a
  // held frame is dropped (freeing its canvas) and the reel goes back to its
  // cover and will start from the cover frame.
  Reels.prototype.tidy = function () {
    var self = this;
    window.clearTimeout(this.tidyTimer);
    this.tiles.forEach(function (t, i) {
      if (i === self.active) {
        return;
      }
      if (self.native) {
        self.tidyTouch(t, i);
        return;
      }
      self.rest(t);
      if (t._snap && t.classList.contains('has-snap') && Math.abs(self.wrap(i - self.active)) > self.visible + 1) {
        t.classList.remove('has-snap');
        t._snap.width = 0;
        t._resume = 0;
      }
    });
    this.primed = null;
    this.warm();
    this.schedulePrime(600);
  };

  // Touch row at rest. No canvas copies here: on iPhones a frame drawn from a
  // paused video can come out blank for a moment, which showed as a flash on
  // the reel beside the centre. A reel still on screen stays the paused video
  // itself (its real frame) and only stops fetching; once it is fully off
  // screen it lets go of its source and returns to its cover, out of sight.
  Reels.prototype.tidyTouch = function (tile, i) {
    var v = tile._video;
    if (!v || v.hasAttribute('data-keep')) {
      return;
    }
    if (!v.paused) {
      v.pause();
    }
    var onScreen = Math.abs(this.centreOffset(i)) < this.half + this.pitch / 2;
    if (onScreen) {
      if (v.getAttribute('src')) {
        v.preload = 'none';
      }
      return;
    }
    if (v.getAttribute('src')) {
      v.removeAttribute('src');
      v.load();
    }
    tile.classList.remove('is-playing', 'has-snap');
    tile._resume = 0;
  };

  Reels.prototype.schedulePrime = function (ms) {
    var self = this;
    window.clearTimeout(this.primeTimer);
    this.primeTimer = window.setTimeout(function () {
      self.prime();
    }, ms);
  };

  // The next reel along, in the direction of the last move, loads its opening
  // frame (preload=metadata) a little after the playing one has started, so
  // a move to it starts at once: measured on a phone swipe, the incoming reel
  // on screen 84ms after the swipe starts against 220ms, and 600ms on 4G.
  // One reel only, dropped at the next rest if the move went the other way:
  // paused downloads add up, and four or five starve new requests on HTTP/3.
  Reels.prototype.prime = function () {
    var cur = this.tiles[this.active];
    if (reduce || !this.inView || document.hidden || !cur._video || cur._video.paused || cur._video.readyState < 3) {
      return;
    }
    var k = this.active + (this.dir || 1);
    if (this.loop) {
      k = ((k % this.n) + this.n) % this.n;
    }
    var tile = this.tiles[k];
    var v = tile && tile._video;
    if (!v || v.getAttribute('src')) {
      return;
    }
    var start = tile._resume || parseFloat(tile.dataset.start || '0');
    v.preload = 'metadata';
    v.src = tile.dataset.src + (start > 0 ? '#t=' + start.toFixed(2) : '');
    this.primed = tile;
  };

  // Once the row is on screen, load the covers and cut-outs either side of the
  // centre so a jump never shows one popping in. Before that, nothing loads.
  Reels.prototype.warm = function () {
    if (!this.inView) {
      return;
    }
    var self = this;
    this.tiles.forEach(function (t, i) {
      if (t._warm) {
        return;
      }
      if (Math.abs(self.wrap(i - self.active)) <= self.visible + 1) {
        t._warm = true;
        Array.prototype.forEach.call(t.querySelectorAll('img[loading="lazy"]'), function (img) {
          img.loading = 'eager';
          if (img.decode) {
            img.decode().catch(function () {});
          }
        });
      }
    });
  };

  // Give the playing reel its source: from its cover frame the first time,
  // from where it stopped if it has played before. A primed reel has it.
  Reels.prototype.arm = function (tile) {
    var v = tile._video;
    if (!v.getAttribute('src') || v.hasAttribute('data-keep')) {
      v.removeAttribute('data-keep');
      var start = tile._resume || parseFloat(tile.dataset.start || '0');
      v.src = tile.dataset.src + (start > 0 ? '#t=' + start.toFixed(2) : '');
    }
    v.preload = 'auto';
  };

  Reels.prototype.playActive = function () {
    var self = this;
    var tile = this.tiles[this.active];
    var v = tile && tile._video;
    if (!v) {
      return;
    }
    if (!this.inView || document.hidden || (reduce && !this.userPlay)) {
      v.pause();
      // Away from the row, the primed reel lets go of its download so the
      // rest of the page loads freely; it is primed again on return.
      window.clearTimeout(this.primeTimer);
      if (this.primed && this.primed !== tile) {
        this.rest(this.primed);
      }
      this.primed = null;
      return;
    }
    this.arm(tile);
    window.clearTimeout(this.watchdog);
    this.watchdog = window.setTimeout(function () {
      if (self.tiles[self.active] !== tile || tile._userPaused || !self.inView || document.hidden || (reduce && !self.userPlay)) {
        return;
      }
      if (v.readyState < 2) {
        // Nothing has arrived yet: the request itself has hung. Ask again.
        self.retry(tile, 'watchdog');
      } else if (v.paused) {
        v.play().catch(function () {});
      }
    }, 3000);
    // Mid swipe the incoming reel starts muted; its sound comes on once the row
    // is at rest (see restoreSound), so a phone switching its audio on never
    // stalls a moving row.
    v.muted = !(this.sound && tile.hasAttribute('data-audio') && !this.holdSound);
    var p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function (err) {
        self.lastPlayError = err && err.name;
        if (self.tiles[self.active] !== tile || tile._video !== v) {
          return; // moved on, or retried on a fresh element
        }
        if (v.muted) {
          // A muted start can still be cut short (a pause landing at the same
          // moment); try once more if this is still the reel to play.
          window.setTimeout(function () {
            if (self.tiles[self.active] === tile && v.paused && self.inView && !document.hidden) {
              v.play().catch(function () {});
            }
          }, 250);
          return;
        }
        // Phones only allow sound to start from a tap, not a swipe. Play this
        // reel muted; the sound button shows it, and one tap turns it on.
        v.muted = true;
        self.setSound(false);
        v.play().catch(function () {});
      });
    }
  };

  // Turn the sound back on for the reel the row has come to rest on. If the
  // phone refuses (no tap behind it) and pauses the video, carry on muted.
  Reels.prototype.restoreSound = function () {
    var tile = this.tiles[this.active];
    var v = tile && tile._video;
    var self = this;
    if (!this.sound || !v || !tile.hasAttribute('data-audio') || !v.muted || v.paused) {
      return;
    }
    v.muted = false;
    window.setTimeout(function () {
      if (v.paused && self.tiles[self.active] === tile && self.inView && !document.hidden) {
        v.muted = true;
        self.setSound(false);
        v.play().catch(function () {});
      }
    }, 150);
  };

  Reels.prototype.setSound = function (on) {
    this.sound = on;
    this.root.classList.toggle('is-sound', on);
    this.tiles.forEach(function (t) {
      if (t._sound) {
        t._sound.setAttribute('aria-pressed', on ? 'true' : 'false');
        t._sound.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
      }
    });
  };

  Reels.prototype.toggleSound = function () {
    var tile = this.tiles[this.active];
    this.setSound(!this.sound);
    this.userPlay = true;
    if (tile._video) {
      tile._video.muted = !this.sound;
      if (tile._video.paused) {
        this.playActive();
      }
    }
  };

  Reels.prototype.centreClick = function () {
    var tile = this.tiles[this.active];
    var v = tile._video;
    if (reduce && v && v.paused) {
      this.userPlay = true;
      this.playActive();
      return;
    }
    if (tile.hasAttribute('data-audio')) {
      this.toggleSound();
    } else if (v) {
      if (v.paused) {
        this.userPlay = true;
        tile._userPaused = false;
        this.playActive();
      } else {
        tile._userPaused = true;
        v.pause();
      }
    }
  };

  Reels.prototype.bindDrag = function () {
    var self = this;
    var start = null;
    var dragging = false;
    var lastX = 0;
    var lastT = 0;
    var vel = 0;

    this.stage.addEventListener('pointerdown', function (e) {
      if (e.button !== 0 || e.target.closest('button')) {
        return;
      }
      start = { x: e.clientX, y: e.clientY, id: e.pointerId };
      dragging = false;
      lastX = e.clientX;
      lastT = performance.now();
      vel = 0;
    });

    this.stage.addEventListener('pointermove', function (e) {
      if (!start || e.pointerId !== start.id) {
        return;
      }
      var dx = e.clientX - start.x;
      var dy = e.clientY - start.y;
      if (!dragging) {
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) {
          return;
        }
        dragging = true;
        // Measure from here, so the row picks up the finger without a jump.
        start.x = e.clientX;
        dx = 0;
        self.root.classList.add('is-dragging');
        // No tidying mid drag; the release schedules it again.
        window.clearTimeout(self.tidyTimer);
        try { self.stage.setPointerCapture(e.pointerId); } catch (err) {}
      }
      var now = performance.now();
      if (now > lastT) {
        vel = (e.clientX - lastX) / (now - lastT);
      }
      lastX = e.clientX;
      lastT = now;
      var f = -dx / self.pitch;
      if (!self.loop) {
        var lo = -self.active;
        var hi = self.n - 1 - self.active;
        if (f < lo) {
          f = lo - (lo - f) * 0.3;
        } else if (f > hi) {
          f = hi + (f - hi) * 0.3;
        }
      }
      self.frac = f;
      self.layout('drag');
    });

    function end(e) {
      if (!start || (e && e.pointerId !== start.id)) {
        return;
      }
      start = null;
      if (!dragging) {
        return;
      }
      dragging = false;
      // A fast flick lets go in the same frame as the last move. Clear any
      // leftover no-transition marks and commit the current positions first,
      // or the glide below would jump straight to the end.
      self.root.classList.remove('is-dragging');
      self.tiles.forEach(function (t) { t.classList.remove('no-anim'); });
      void self.stage.offsetWidth;
      // Distance decides how far it moves; a quick flick that travelled less
      // than half a tile still moves one in the direction of the flick.
      var step = Math.round(self.frac);
      if (step === 0 && Math.abs(vel) > 0.35 && Math.abs(self.frac) > 0.05) {
        step = vel < 0 ? 1 : -1;
      }
      var target = self.active + step;
      // Carry the finger's speed into the glide: the ease-out starts at about
      // 2.8x its average speed, so pick the duration that starts at the
      // speed the finger let go at (within 220 to 500ms).
      var remaining = Math.abs(target - self.active - self.frac) * self.pitch;
      var speed = Math.abs(vel);
      var ms = speed > 0.15 && remaining > 1 ? (2.8 * remaining) / speed : 500;
      self.setGlide(Math.max(220, Math.min(500, ms)));
      self.suppressClick = true;
      window.setTimeout(function () { self.suppressClick = false; }, 60);
      self.go(target);
    }

    this.stage.addEventListener('pointerup', end);
    this.stage.addEventListener('pointercancel', end);
  };

  // Trackpad two-finger swipe (and shift + mouse wheel) on mouse screens: the
  // row follows the fingers like a drag, momentum included, and once the
  // wheel events stop it settles on the nearest reel, so a flick can carry
  // past more than one. Vertical scrolling is left to the page.
  Reels.prototype.bindWheel = function () {
    var self = this;
    var quiet = null;
    var moving = false;
    this.stage.addEventListener('wheel', function (e) {
      var unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? self.pitch : 1;
      var dx = e.deltaX * unit;
      var dy = e.deltaY * unit;
      if (e.shiftKey && !dx) {
        dx = dy;
        dy = 0;
      }
      if (!moving && Math.abs(dx) <= Math.abs(dy)) {
        return;
      }
      e.preventDefault();
      if (!moving) {
        moving = true;
        self.root.classList.add('is-dragging');
        window.clearTimeout(self.tidyTimer);
      }
      var f = self.frac + dx / self.pitch;
      // One flick moves three reels at most.
      f = Math.max(-3, Math.min(3, f));
      if (!self.loop) {
        var lo = -self.active;
        var hi = self.n - 1 - self.active;
        f = Math.max(lo - 0.3, Math.min(hi + 0.3, f));
      }
      self.frac = f;
      self.layout('drag');
      window.clearTimeout(quiet);
      quiet = window.setTimeout(function () {
        moving = false;
        self.root.classList.remove('is-dragging');
        self.tiles.forEach(function (t) { t.classList.remove('no-anim'); });
        void self.stage.offsetWidth;
        var step = Math.round(self.frac);
        if (step === 0 && Math.abs(self.frac) > 0.12) {
          step = self.frac > 0 ? 1 : -1;
        }
        self.setGlide(320);
        self.go(self.active + step);
      }, 140);
    }, { passive: false });
  };

  // Native row: the scroll handler. As soon as the row has moved 12px, the
  // reel it is heading for takes over (a swipe goes one along in its
  // direction). After a short quiet spell (scroll events keep firing through
  // iOS momentum) the row is at rest and settle() corrects and tidies.
  Reels.prototype.bindScroll = function () {
    var self = this;
    var quiet;
    var frame = null;
    this.track.addEventListener('scroll', function () {
      var sl = self.track.scrollLeft;
      if (self.from === null) {
        self.from = sl;
      }
      if (!self.handed && Math.abs(sl - self.from) > 12) {
        self.handed = true;
        self.holdSound = true;
        self.handover(Math.max(0, Math.min(self.n - 1, self.active + (sl > self.from ? 1 : -1))));
      }
      if (!frame) {
        frame = window.requestAnimationFrame(function () {
          frame = null;
          self.scaleFrame();
        });
      }
      window.clearTimeout(quiet);
      quiet = window.setTimeout(function () {
        self.from = null;
        self.handed = false;
        self.settle();
        self.holdSound = false;
        self.restoreSound();
      }, 120);
    }, { passive: true });
  };

  Reels.prototype.init = function () {
    var self = this;
    if (!this.n) {
      return;
    }
    this.tiles.forEach(function (t) {
      self.setupTile(t);
    });
    this.ensureLoop();

    // Dots when they fit on one line, arrows always.
    if (this.dotsWrap && this.count <= 20) {
      for (var i = 0; i < this.count; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'ydm-reels__dot';
        b.setAttribute('aria-label', 'Go to reel ' + (i + 1));
        b.dataset.index = String(i);
        this.dotsWrap.appendChild(b);
        this.dots.push(b);
      }
    }
    if (this.nav) {
      this.nav.classList.add('has-js');
      this.nav.addEventListener('click', function (e) {
        var dot = e.target.closest('.ydm-reels__dot');
        self.setGlide(500);
        if (dot) {
          self.goOrig(parseInt(dot.dataset.index, 10));
        } else if (e.target.closest('[data-reels-prev]')) {
          self.go(self.active - 1);
        } else if (e.target.closest('[data-reels-next]')) {
          self.go(self.active + 1);
        }
      });
    }

    this.root.classList.add(this.native ? 'is-native' : 'is-ready');
    this.stage.tabIndex = 0;
    this.fitLabels();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { self.fitLabels(); });
    }
    this.measure();
    this.layout(true);
    this.markCentre();
    this.updateNav();

    this.stage.addEventListener('click', function (e) {
      if (self.suppressClick) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      var tile = e.target.closest('.ydm-reel');
      if (!tile) {
        return;
      }
      var i = self.tiles.indexOf(tile);
      if (e.target.closest('.ydm-reel__sound')) {
        self.toggleSound();
        return;
      }
      if (e.target.closest('.ydm-reel__card') && tile._card) {
        return; // the card's links go to the model or the floor plan
      }
      if (i !== self.active) {
        self.setGlide(500);
        self.go(i);
      } else {
        self.centreClick();
      }
    }, true);

    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        self.setGlide(500);
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        self.go(self.active - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        self.go(self.active + 1);
      }
    });

    if (this.native) {
      this.bindScroll();
      this.cacheCentres();
      // Start on the first reel of the middle set, centred.
      if (this.sets && this.n === this.count * this.sets) {
        this.active = Math.floor(this.sets / 2) * this.count;
      }
      this.track.scrollLeft = this.tileLeft(this.active);
      this.scaleFrame();
      this.markCentre();
      this.updateNav();
    } else {
      this.bindDrag();
      this.bindWheel();
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          self.inView = en.isIntersecting && en.intersectionRatio >= 0.35;
          self.warm();
          self.playActive();
        });
      }, { threshold: [0, 0.35, 0.6] }).observe(this.stage);
    } else {
      this.inView = true;
      this.warm();
      this.playActive();
    }

    document.addEventListener('visibilitychange', function () {
      self.playActive();
    });

    var rt;
    window.addEventListener('resize', function () {
      window.clearTimeout(rt);
      rt = window.setTimeout(function () {
        self.ensureLoop();
        self.fitLabels();
        self.measure();
        if (!self.loop) {
          self.active = Math.max(0, Math.min(self.n - 1, self.active));
        }
        self.layout(true);
        if (self.native) {
          self.cacheCentres();
          self.track.scrollLeft = self.tileLeft(self.active);
          self.scaleFrame();
        }
        self.markCentre();
        self.updateNav();
      }, 120);
    });
  };

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.ydm-reels'), function (el) {
      if (!el._reels) {
        el._reels = new Reels(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
